import React, { useState, useEffect, useMemo } from "react";
import bb from "../../../assets/img/Box_all.gif";

import DSBox from "../../system-design/DSBox";
import DSCard from "../../system-design/DSCard";
import DSCardContent from "../../system-design/DSCardContent";
import DSTypography from "../../system-design/DSTypography";
import DSButton from "../../system-design/DSButton";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSIconButton from "../../system-design/DSIconButton";
import DSTextField from "../../system-design/DSTextField";
import DSCheckBox from "../../system-design/DSCheckBox";
import DSFormControlLabel from "../../system-design/DSFormControlLabel";
import DSTooltip from "../../system-design/DSTooltip";
import DSDialog from "../../system-design/DSDialog";
import DSDialogTitle from "../../system-design/DSDialogTitle";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSDialogActions from "../../system-design/DSDialogActions";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import KlydoListItem from "../../KlydoListItem";
import firebaseService from "../../../firebase_service/firebaseService";
import ButtonLoader from "../../../components/Widgets/ButtonLoader";
import SimpleSwitch from "../../../components/Widgets/SimpleSwitch";
import SimpleBar from "../../../components/Widgets/SimpleBar";
import SimpleDropdown from "../../../components/Widgets/SimpleDropdown";
import { secondsToTime } from "../../../utils/util";
import SimpleTimePicker from "../../../components/Widgets/SimpleTimePicker";
import {
  DevMode,
  Device,
  Klydo,
  NightModeAppearance,
  colType,
} from "../../../Types";
import DSSectionHeading from "../../system-design/DSSectionHeading";
import { sectionCardSx } from "../../system-design/sectionCardSx";
import MsTable from "../../../components/Widgets/Table";
import { NavLink } from "react-router-dom";
import useDeviceModal from "../hooks/useDeviceModal";
import useGetLastVersionNumber from "../hooks/useGetLastVersionNumber";
import DevicesCommands from "./DevicesCommands";
import ClockTags from "./ClockTags";
import DeviceCollectionsWidget from "./DeviceCollectionsWidget";
import { DeviceProvider } from "../context/DeviceModalContext";

import DeviceFiltersCard from "./device-config/DeviceFiltersCard";
import DeviceEcoModeCard from "./device-config/DeviceEcoModeCard";
import DeviceConfigOverridesCard from "./device-config/DeviceConfigOverridesCard";
import DeviceFavoriteCard from "./device-config/DeviceFavoriteCard";
import DeviceOnlineCard from "./device-config/DeviceOnline/DeviceOnlineCard";
import DeviceErrorsCard from "./device-config/DeviceErrors/DeviceErrorsCard";
import DeviceMomentsCard from "./device-config/DeviceMoments/DeviceMomentsCard";
import {
  DeviceCommandsHistoryCard,
  DevicePendingCommandsCard,
} from "./device-config/DeviceCommands/DeviceCommandsCards";
import DeviceInfoCard from "./device-config/DeviceInfoCard";
import DeviceContentProfilesCard from "./device-config/DeviceContentProfilesCard";
import DeviceHiddenKlydosCard from "./device-config/DeviceHiddenKlydosCard";
import DevicePendingKlydosCard from "./device-config/DevicePendingKlydosCard";
import DeviceDefaultChimeCard from "./device-config/DeviceDefaultChimeCard";
import { useQueries } from "@tanstack/react-query";
import klydoApiClient from "../../../api/klydo";

const NightMode = ["AUTO", "SCHEDULE", "OFF"];
const Show = [
  "FIXED",
  "DAY",
  "SIX_HOURS",
  "HOUR",
  "MINUTE",
  "QUARTER",
  "FIVE_MINUTES",
];
const ViewType = ["FEED", "FAVORITE", "COLLECTIONS"];
const ScreenState = ["OFF", "DEFAULT", "NIGHTMODE", "ECO"];
const ChimeMode = ["ONLY_CHIMES", "ONLY_STRIKES", "STRIKES_AND_CHIMES"];

// Firmware versions that introduced these settings (see clock-application-v3)
const STAY_IN_FAVORITES_MIN_VERSION = 604;
const NIGHT_MODE_APPEARANCE_MIN_VERSION = 615.3;
// devMode (OFF / DEV / ADB_ONLY) replaces the hideDev switch from this version
const DEV_MODE_MIN_VERSION = 644.1;

// Hover tooltip explaining why a control is disabled for this device version.
// Disabled inputs swallow mouse events, so the trigger wraps them in a div.
const VersionTooltip = (props: {
  active: boolean;
  message: string;
  children: React.ReactElement;
}) => (
  <DSTooltip placement="top" title={props.active ? props.message : ""}>
    <div>{props.children}</div>
  </DSTooltip>
);

// Muted label for inline label/value rows (caption variant renders a span)
const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <DSTypography
    variant="caption"
    sx={{ color: "text.secondary", fontSize: "13px", fontWeight: 600 }}
  >
    {children}
  </DSTypography>
);

const collectionTypeOptions = ["All", "Favorite", "My Klydos", "Premium"];

const TIME_OFFSET_DEBOUNCE_MS = 800;

export type DeviceModalProps = {
  setShowModal: (show: boolean) => void;
  row: Device;
};

function DeviceModal({ setShowModal, row }: DeviceModalProps) {
  const [currentFeedsPage, setCurrentFeedsPage] = useState(0);
  const [feedsSearch, setFeedsSearch] = useState("");
  const {
    data,
    isLoading,
    showKlydoViewingModal,
    setShowKlydoViewingModal,
    gift,
    deleteGift,
    detachClock,
    hideDev,
    currentKlydo,
    timeOffset,
    updateDevApp,
    setUpdateDevApp,
    updateVersion,
    setUpdateVersion,
    showUpdateModal,
    setShowUpdateModal,
    updateAppLoader,
    setUpdateAppLoader,
    schedualOn,
    klydoAnalyticData,
    setKlydoAnalyticData,
    tableId,
    refetch,
    handleRef,
  } = useDeviceModal(row.id);

  // Time offset is written once typing stops, not per keystroke: typing
  // "-30" used to write "-" (NaN), then -3, then -30. Only whole minutes in
  // the firmware's range (BoundedIntConverter, -1440..1440) are written.
  const [timeOffsetInput, setTimeOffsetInput] = useState<string | null>(null);
  const timeOffsetMinutes =
    timeOffsetInput === null || timeOffsetInput.trim() === ""
      ? null
      : Number(timeOffsetInput);
  const timeOffsetInvalid =
    timeOffsetInput !== null &&
    (timeOffsetMinutes === null ||
      !Number.isInteger(timeOffsetMinutes) ||
      Math.abs(timeOffsetMinutes) > 1440);
  useEffect(() => {
    if (!data || timeOffsetMinutes === null || timeOffsetInvalid) return;
    const timer = setTimeout(() => {
      firebaseService
        .updateConfig(data.id, "timeOffset", timeOffsetMinutes)
        .then(() => {
          data.timeOffset = { time: new Date(), value: timeOffsetMinutes };
        });
    }, TIME_OFFSET_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [timeOffsetMinutes, timeOffsetInvalid]);

  // A clock that has never reported a `version` has nothing to compare a
  // target version against, so this would render blank either way. Skip the
  // request: on the API side a version-less lookup means the clock would be
  // served the wrong build, and it is reported as an error there. A configured
  // `hardVersion` is the exception - that answer is meaningful on its own.
  const { data: lastVersionNumber, refetch: refetchLastVersion } =
    useGetLastVersionNumber(
      row.id,
      row.version != null || data?.hardVersion != null,
    );

  const feedIds = data?.feeds?.value || [];

  const feedKlydoQueries = useQueries({
    queries: feedIds.map((id) => ({
      queryKey: ["klydo", id],
      queryFn: () => klydoApiClient.getKlydoById(id),
    })),
  });

  const feedKlydoMap = useMemo(() => {
    const map = new Map<string, Klydo>();
    feedIds.forEach((id, i) => {
      const klydo = feedKlydoQueries[i]?.data;
      if (klydo) map.set(id, klydo);
    });
    return map;
  }, [feedIds, feedKlydoQueries]);

  const filteredFeedIds = useMemo(() => {
    const term = feedsSearch.trim().toLowerCase();
    if (!term) return feedIds;
    return feedIds.filter((id) => {
      if (id.toLowerCase().includes(term)) return true;
      const klydo = feedKlydoMap.get(id);
      if (!klydo) return false;
      return (
        klydo.name?.toLowerCase().includes(term) ||
        klydo.author?.name?.toLowerCase().includes(term)
      );
    });
  }, [feedIds, feedsSearch, feedKlydoMap]);

  const [hardVersionEnabled, setHardVersionEnabled] = useState(
    !!data?.hardVersion,
  );
  const [hardVersionValue, setHardVersionValue] = useState(
    data?.hardVersion?.version || "",
  );
  const [hardVersionPermanent, setHardVersionPermanent] = useState(
    data?.hardVersion?.permanent || false,
  );
  const [hardVersionModified, setHardVersionModified] = useState(false);

  useEffect(() => {
    if (data) {
      setHardVersionEnabled(!!data.hardVersion);
      setHardVersionValue(data.hardVersion?.version || "");
      setHardVersionPermanent(data.hardVersion?.permanent || false);
    }
  }, [data]);

  const imgOrVideo = (klydo: Klydo) => {
    if (klydo.id === "9d371dd0-f44a-4d4d-8aeb-a927a788c978")
      return (
        <img
          style={{ borderRadius: 50 + "%" }}
          loading="lazy"
          height={96}
          width={96}
          src={bb}
          alt="klydo"
        />
      );
    const url = klydo.klydoAnimation?.m;
    if (url) {
      return (
        <video
          muted={true}
          style={{ borderRadius: 50 + "%" }}
          loop
          autoPlay
          height={96}
          width={96}
          src={url}
        />
      );
    }
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <p>File type not supported</p>
      </div>
    );
  };

  const tableColumn: Array<colType> = [
    {
      sort: true,
      label: "Clock Time",
      field: "clockTime",
    },
    {
      size: 170,
      sort: true,
      label: "Display Time (mos:day:hrs:min:sec)",
      field: "displayTime",
    },
    {
      sort: true,
      label: "Klydo Id",
      field: "idf",
    },
    {
      sort: true,
      label: "Auto Switch",
      field: "autoSwitch",
    },
    {
      sort: true,
      label: "Favorite",
      field: "favorite",
    },
    {
      sort: true,
      label: "Collection Type",
      field: "collectionType",
    },
  ];
  const tableRows = (
    r: {
      clockTime: Date;
      displayTime: number;
      klydo?: Klydo;
      autoSwitch: boolean;
      favorite: boolean;
      collectionType: number;
    },
    key: string,
  ) => {
    return {
      clockTime: r.clockTime.toLocaleString("en-GB"),
      displayTime: secondsToTime(r.displayTime),
      idf: (
        <DSTooltip
          placement="top"
          title={r.klydo ? imgOrVideo(r.klydo) : "no-klydo"}
        >
          <NavLink
            to={
              r.klydo ? `/admin/klydos?search=${r.klydo.id}` : "/admin/klydos"
            }
          >
            {r.klydo?.id || "-No klydo available"}
          </NavLink>
        </DSTooltip>
      ),
      autoSwitch: r.autoSwitch?.toString(),
      favorite: r.favorite?.toString(),
      collectionType: collectionTypeOptions[r.collectionType],
      key: key,
    };
  };

  if (!data || isLoading) return null;

  // Version 592.21+ uses 0-100 for volume values, older versions use 0-1
  const isNewVolumeRange = data.version && data.version >= 592.21;

  return (
    <>
      <DSDialogTitle
        variant="h5"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid",
          borderBottomColor: "divider",
        }}
      >
        Device config
        <DSIconButton aria-label="close" onClick={() => setShowModal(false)}>
          <CloseIcon />
        </DSIconButton>
      </DSDialogTitle>
      <DSDialogContent>
        <DSBox
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xxs: "1fr",
              md: "minmax(0, 1fr) minmax(0, 1fr)",
            },
            gap: 2,
            alignItems: "start",
          }}
        >
          <DSBox sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
            <DSBox>
              <DeviceProvider
                deviceId={data.id}
                onClose={() => setShowModal(false)}
              >
                <DeviceInfoCard />
              </DeviceProvider>
              <DeviceContentProfilesCard deviceId={data.id} />
            </DSBox>
            <DSBox>
              <DSCard sx={[sectionCardSx, { mb: 2 }]}>
                <DSSectionHeading>Show Klydos</DSSectionHeading>
                <DSCardContent>
                  <DSBox
                    title="Switch Fresh Hold on/off"
                    sx={{ display: "flex", ml: "20px" }}
                  >
                    <SimpleSwitch
                      title="Fresh Hold"
                      on={undefined}
                      default={!!data?.freshHold?.value}
                      disabled={!data.version || data.version < 480.4}
                      onClick={() =>
                        firebaseService.updateConfig(
                          data.id,
                          "freshHold",
                          !data?.freshHold?.value,
                        )
                      }
                    />
                  </DSBox>
                  <DSBox
                    title="Enable this clock to rate klydos (reviews feed)"
                    sx={{ display: "flex", ml: "20px" }}
                  >
                    <SimpleSwitch
                      title="Klydos Rater"
                      on={undefined}
                      default={!!data?.isKlydosRater?.value}
                      onClick={() =>
                        firebaseService.updateConfig(
                          data.id,
                          "isKlydosRater",
                          !data?.isKlydosRater?.value,
                        )
                      }
                    />
                  </DSBox>
                  <DSBox title="Show mode: fixed Klydo or changes every hour/day">
                    <VersionTooltip
                      active={!!data.version && data.version >= 606}
                      message="Replaced by Feed/Collection/Favorites Cycle Mode in version ≥ 606"
                    >
                      <SimpleDropdown
                        title="Show: "
                        list={Show}
                        default={data.show ? data.show.value : Show[1]}
                        disabled={!!data.version && data.version >= 606}
                        onSelect={(s: string) => {
                          firebaseService
                            .updateConfig(data.id, "show", s)
                            .then(() => {
                              data.show = { time: new Date(), value: s };
                            })
                            .catch((e) =>
                              console.error("Failed to update show", e),
                            );
                        }}
                      />
                    </VersionTooltip>
                  </DSBox>
                  <DSBox title="Feed cycle mode: fixed Klydo or changes every hour/day">
                    <SimpleDropdown
                      title={
                        data.version && data.version < 606
                          ? "Feed Cycle Mode*: "
                          : "Feed Cycle Mode: "
                      }
                      list={Show}
                      default={
                        data.feedCycleMode ? data.feedCycleMode.value : Show[1]
                      }
                      onSelect={(s: string) => {
                        firebaseService
                          .updateConfig(data.id, "feedCycleMode", s)
                          .then(() => {
                            data.feedCycleMode = {
                              time: new Date(),
                              value: s,
                            };
                          });
                      }}
                    />
                    {data.version && data.version < 606 && (
                      <DSTypography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        * Available in version &ge; 606
                      </DSTypography>
                    )}
                  </DSBox>
                  <DSBox title="Collection cycle mode: fixed Klydo or changes every hour/day">
                    <SimpleDropdown
                      title={
                        data.version && data.version < 606
                          ? "Collection Cycle Mode*: "
                          : "Collection Cycle Mode: "
                      }
                      list={Show}
                      default={
                        data.collectionCycleMode
                          ? data.collectionCycleMode.value
                          : Show[1]
                      }
                      onSelect={(s: string) => {
                        firebaseService
                          .updateConfig(data.id, "collectionCycleMode", s)
                          .then(() => {
                            data.collectionCycleMode = {
                              time: new Date(),
                              value: s,
                            };
                          });
                      }}
                    />
                    {data.version && data.version < 606 && (
                      <DSTypography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        * Available in version &ge; 606
                      </DSTypography>
                    )}
                  </DSBox>
                  <DSBox title="Favorites cycle mode: fixed Klydo or changes every hour/day">
                    <SimpleDropdown
                      title={
                        data.version && data.version < 606
                          ? "Favorites Cycle Mode*: "
                          : "Favorites Cycle Mode: "
                      }
                      list={Show}
                      default={
                        data.favoritesCycleMode
                          ? data.favoritesCycleMode.value
                          : Show[1]
                      }
                      onSelect={(s: string) => {
                        firebaseService
                          .updateConfig(data.id, "favoritesCycleMode", s)
                          .then(() => {
                            data.favoritesCycleMode = {
                              time: new Date(),
                              value: s,
                            };
                          });
                      }}
                    />
                    {data.version && data.version < 606 && (
                      <DSTypography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        * Available in version &ge; 606
                      </DSTypography>
                    )}
                  </DSBox>
                  <DSBox
                    title="Return favorited Klydos to the feed after a duration"
                    sx={{ display: "flex", ml: "20px" }}
                  >
                    <VersionTooltip
                      active={
                        !data.version ||
                        data.version < STAY_IN_FAVORITES_MIN_VERSION
                      }
                      message={`Available in version ≥ ${STAY_IN_FAVORITES_MIN_VERSION}`}
                    >
                      <SimpleSwitch
                        title="Stay in Favorites"
                        on={undefined}
                        default={!!data?.stayInFavorits?.value}
                        disabled={
                          !data.version ||
                          data.version < STAY_IN_FAVORITES_MIN_VERSION
                        }
                        onClick={(on: boolean) => {
                          firebaseService
                            .updateConfig(data.id, "stayInFavorits", on)
                            .then(() => {
                              data.stayInFavorits = {
                                time: new Date(),
                                value: on,
                              };
                            })
                            .catch((e) =>
                              console.error(
                                "Failed to update stayInFavorits",
                                e,
                              ),
                            );
                        }}
                      />
                    </VersionTooltip>
                  </DSBox>
                  <DSBox title="Display mode: all Klydos or device favorites">
                    <SimpleDropdown
                      title="Display Mode: "
                      list={(() => {
                        const list = [...ViewType];
                        if (data.user?.value) list.push("MYKLYDOS");
                        return list;
                      })()}
                      default={data.mode ? data.mode.value : ViewType[0]}
                      disabled={!!data.version && data.version < 481.46}
                      onSelect={function (s: string): void {
                        firebaseService.updateConfig(data.id, "mode", s);
                      }}
                    />
                  </DSBox>
                </DSCardContent>
              </DSCard>
            </DSBox>
            <DSBox>
              <DSCard sx={[sectionCardSx, { mb: 2 }]}>
                <DSSectionHeading>Display</DSSectionHeading>
                <DSCardContent>
                  <DSBox title="Screen brightness">
                    <SimpleBar
                      title={"Brightness:"}
                      onChange={(e) => {
                        firebaseService
                          .updateConfig(
                            data.id,
                            "brightness",
                            Number(e.target.value),
                          )
                          .then(
                            () =>
                              (data.brightness = {
                                time: new Date(),
                                value: Number(e.target.value),
                              }),
                          );
                      }}
                      max={10}
                      default={data.brightness ? data.brightness.value : 10}
                    />
                  </DSBox>
                  {/* <DSBox
                  title="Switch screen sensor on/off"
                  sx={{ display: "flex", ml: "20px" }}
                >
                  <SimpleSwitch
                    title="Sensor Screen"
                    on={undefined}
                    default={data.auto ? data.auto.value : false}
                    onClick={(on) =>
                      firebaseService.updateConfig(data.id, "auto", on)
                    }
                  />
                </DSBox> */}
                  <DSBox
                    title="Night mode: on schedual \ auto \ off"
                    sx={{ mt: 1 }}
                  >
                    <SimpleDropdown
                      title="Night mode: "
                      list={NightMode}
                      default={
                        data.nightMode ? data.nightMode.value : NightMode[2]
                      }
                      onSelect={(s: string) => {
                        firebaseService
                          .updateConfig(data.id, "nightMode", s)
                          .then(
                            () =>
                              (data.nightMode = {
                                time: new Date(),
                                value: s,
                              }),
                          );
                      }}
                    />
                  </DSBox>
                  {schedualOn && (
                    <DSBox title="Night Mode Times" sx={{ mt: 1, mb: 1 }}>
                      <DSBox
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 2,
                          pl: "20px",
                        }}
                      >
                        <DSBox>
                          <SimpleTimePicker
                            title={"From:"}
                            onChange={(e) => {
                              firebaseService
                                .updateConfig(data.id, "nightModeTimes", {
                                  from: e.target.value,
                                  until: data.nightModeTimes
                                    ? data.nightModeTimes.value.until
                                    : "07:00",
                                })
                                .then(
                                  () =>
                                    (data.nightModeTimes = {
                                      time: new Date(),
                                      value: {
                                        from: e.target.value,
                                        until: data.nightModeTimes
                                          ? data.nightModeTimes.value.until
                                          : "07:00",
                                      },
                                    }),
                                );
                            }}
                            default={
                              data.nightModeTimes
                                ? data.nightModeTimes.value.from
                                : "22:00"
                            }
                          />
                        </DSBox>
                        <DSBox>
                          <SimpleTimePicker
                            title={"Until:"}
                            onChange={(e) => {
                              firebaseService
                                .updateConfig(data.id, "nightModeTimes", {
                                  from: data.nightModeTimes
                                    ? data.nightModeTimes.value.from
                                    : "22:00",
                                  until: e.target.value,
                                })
                                .then(
                                  () =>
                                    (data.nightModeTimes = {
                                      time: new Date(),
                                      value: {
                                        from: data.nightModeTimes
                                          ? data.nightModeTimes.value.from
                                          : "22:00",
                                        until: e.target.value,
                                      },
                                    }),
                                );
                            }}
                            default={
                              data.nightModeTimes
                                ? data.nightModeTimes.value.until
                                : "07:00"
                            }
                          />
                        </DSBox>
                      </DSBox>
                    </DSBox>
                  )}
                  <DSBox
                    title="Night mode appearance: red theme / dimmed red / screen off"
                    sx={{ mt: 1 }}
                  >
                    <VersionTooltip
                      active={
                        !data.version ||
                        data.version < NIGHT_MODE_APPEARANCE_MIN_VERSION
                      }
                      message={`Available in version ≥ ${NIGHT_MODE_APPEARANCE_MIN_VERSION}`}
                    >
                      <SimpleDropdown
                        title="Night Mode Appearance: "
                        list={Object.values(NightModeAppearance)}
                        default={
                          data.nightModeAppearance
                            ? data.nightModeAppearance.value
                            : NightModeAppearance.RED_THEME
                        }
                        disabled={
                          !data.version ||
                          data.version < NIGHT_MODE_APPEARANCE_MIN_VERSION
                        }
                        onSelect={(s: string) => {
                          firebaseService
                            .updateConfig(data.id, "nightModeAppearance", s)
                            .then(() => {
                              data.nightModeAppearance = {
                                time: new Date(),
                                value: s as NightModeAppearance,
                              };
                            })
                            .catch((e) =>
                              console.error(
                                "Failed to update nightModeAppearance",
                                e,
                              ),
                            );
                        }}
                      />
                    </VersionTooltip>
                  </DSBox>
                  <DSBox
                    title="Screen state: OFF / DEFAULT / NIGHTMODE / ECO"
                    sx={{ mt: 1 }}
                  >
                    <SimpleDropdown
                      title="Screen State: "
                      list={ScreenState}
                      default={
                        data.screenState
                          ? data.screenState.value
                          : ScreenState[1]
                      }
                      disabled={!data.version || data.version < 590}
                      onSelect={(s: string) => {
                        firebaseService
                          .updateConfig(data.id, "screenState", s)
                          .then(
                            () =>
                              (data.screenState = {
                                time: new Date(),
                                value: s,
                              }),
                          );
                      }}
                    />
                  </DSBox>
                  <DSBox
                    title="Show dials on/off"
                    sx={{ display: "flex", ml: "20px", mt: 1 }}
                  >
                    <SimpleSwitch
                      title="Show Dials"
                      on={undefined}
                      default={data.showDials?.value ?? false}
                      onClick={(on) =>
                        firebaseService
                          .updateConfig(data.id, "showDials", on)
                          .then(
                            () =>
                              (data.showDials = {
                                time: new Date(),
                                value: on,
                              }),
                          )
                      }
                    />
                  </DSBox>
                </DSCardContent>
              </DSCard>

              <DeviceProvider
                deviceId={row.id}
                onClose={() => setShowModal(false)}
              >
                <DeviceFiltersCard />
                <DeviceEcoModeCard />
                <DeviceConfigOverridesCard />
                <DeviceOnlineCard />
              </DeviceProvider>
            </DSBox>
            <DSBox>
              <DSCard sx={[sectionCardSx, { pb: "15px", mb: 2 }]}>
                <DSSectionHeading>Tags</DSSectionHeading>
                <DSCardContent>
                  <ClockTags clock={data} />
                </DSCardContent>
              </DSCard>
            </DSBox>
            <DSBox>
              <DeviceProvider
                deviceId={data.id}
                onClose={() => setShowModal(false)}
              >
                <DeviceHiddenKlydosCard />
              </DeviceProvider>
              <DeviceCollectionsWidget
                title="Collections"
                fieldName="machineCollections"
                deviceId={data.id}
                data={data}
                refetch={refetch}
              />
            </DSBox>
          </DSBox>
          <DSBox sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
            <DSBox>
              <DSCard sx={[sectionCardSx, { mb: 2 }]}>
                <DSSectionHeading>Klydo</DSSectionHeading>
                <DSCardContent>
                  <DSBox
                    title="Link to the current displayed Klydo in the editor"
                    sx={{ ml: "20px" }}
                  >
                    <FieldLabel>Current Klydo: </FieldLabel>
                    <DSBox
                      sx={{
                        display: "flex",
                        gap: "10px",
                        flexDirection: "row",
                        justifyContent: "space-between",
                        p: "10px",
                      }}
                    >
                      <DSBox sx={{ display: "flex", flexDirection: "column" }}>
                        <a
                          style={{ display: "inline" }}
                          target="_blank"
                          rel="noreferrer"
                          href={handleRef()}
                        >
                          <b>Open in editor</b>
                        </a>
                        <NavLink to={`/admin/klydos?search=${currentKlydo}`}>
                          <b>View in Klydos table</b>
                        </NavLink>
                        <DSBox
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mt: "10px",
                          }}
                        >
                          <b>{data?.klydo?.id}</b>
                        </DSBox>
                        <DSPrimaryButton
                          sx={{
                            display: "inline-block",
                            mt: "15px",
                            width: "200px",
                          }}
                          onClick={async () => {
                            setShowKlydoViewingModal(true);
                            firebaseService
                              .getKlydoAnalyticsByDevice(data.id)
                              .then(async (v) => {
                                const klydos =
                                  await firebaseService.getData<Klydo>(
                                    "klydos",
                                  );
                                setKlydoAnalyticData(
                                  v.map((a) => ({
                                    clockTime: new Date(a.clock_time),
                                    displayTime: a.display_time,
                                    klydo: klydos?.find(
                                      (k) => k.id === a.klydo_id,
                                    ),
                                    autoSwitch: a.auto_switch?.toString(),
                                    favorite: a.favorite?.toString(),
                                    collectionType: a.collection_type,
                                  })),
                                );
                              });
                          }}
                        >
                          Klydo viewing history
                        </DSPrimaryButton>
                      </DSBox>
                      {data?.klydo?.loopUrl && (
                        <video
                          muted={true}
                          style={{ borderRadius: "50%" }}
                          loop
                          autoPlay
                          playsInline
                          height={96}
                          width={96}
                          src={data.klydo.loopUrl}
                        />
                      )}
                    </DSBox>
                  </DSBox>
                  {(data.curCollection?.value ||
                    data.curCollections?.value ||
                    data.curFavs?.value ||
                    data.curFeed?.value ||
                    data.tag?.value) && (
                    <DSBox sx={{ ml: "20px", mt: "15px" }}>
                      <DSBox
                        sx={{
                          p: "12px",
                          borderRadius: "4px",
                        }}
                      >
                        {data.curCollection?.value && (
                          <DSBox sx={{ py: "5px" }}>
                            <FieldLabel>Current Collection:</FieldLabel>{" "}
                            {data.curCollection.value}
                          </DSBox>
                        )}
                        {data.curCollections?.value && (
                          <DSBox sx={{ py: "5px" }}>
                            <FieldLabel>Current Collections:</FieldLabel>{" "}
                            {data.curCollections.value}
                          </DSBox>
                        )}
                        {data.curFavs?.value && (
                          <DSBox sx={{ py: "5px" }}>
                            <FieldLabel>Current Favorites:</FieldLabel>{" "}
                            {data.curFavs.value}
                          </DSBox>
                        )}
                        {data.curFeed?.value && (
                          <DSBox sx={{ py: "5px" }}>
                            <FieldLabel>Current Feed:</FieldLabel>{" "}
                            {data.curFeed.value}
                          </DSBox>
                        )}
                        {data.tag?.value && (
                          <DSBox sx={{ py: "5px" }}>
                            <FieldLabel>Tag:</FieldLabel> {data.tag.value}
                          </DSBox>
                        )}
                      </DSBox>
                    </DSBox>
                  )}
                </DSCardContent>
              </DSCard>
              <DSCard sx={[sectionCardSx, { mb: 2 }]}>
                <DSSectionHeading>Gift</DSSectionHeading>
                <DSCardContent sx={{ pb: "10px" }}>
                  {gift && (
                    <>
                      <DSTypography sx={{ display: "inline" }}>
                        <FieldLabel>Klydo:</FieldLabel>{" "}
                        <NavLink to={`/admin/klydos?search=${gift!.klydo.id}`}>
                          <b>{gift!.klydo.name}</b>
                        </NavLink>
                      </DSTypography>
                      <br />
                      <DSTypography sx={{ display: "inline" }}>
                        <FieldLabel>Letter:</FieldLabel> {gift?.greetingContent}
                      </DSTypography>
                      <br />
                      <DSTypography sx={{ display: "inline" }}>
                        <FieldLabel>From:</FieldLabel> {gift?.senderName}
                      </DSTypography>
                      <br />
                      <DSTypography sx={{ display: "inline" }}>
                        <FieldLabel>Date:</FieldLabel>{" "}
                        {gift?.date
                          ? new Date(gift.date).toLocaleDateString("default", {
                              month: "long",
                              day: "numeric",
                              year: "numeric",
                            })
                          : "No Date"}
                      </DSTypography>
                      <br />
                      <DSBox sx={{ display: "inline" }}>
                        <FieldLabel>Sender Image Url: </FieldLabel>
                        {gift?.senderImageUrl ? (
                          <img
                            onClick={() => window.open(gift?.senderImageUrl)}
                            width={20}
                            src={gift.senderImageUrl}
                            title="Go to image"
                          />
                        ) : (
                          "No Image"
                        )}
                      </DSBox>
                      <br />
                      <ButtonLoader
                        variant="danger"
                        title="Delete Gift"
                        onClick={() => deleteGift()}
                      />
                      <ButtonLoader
                        variant="warning"
                        title="Detach Clock"
                        onClick={() => detachClock()}
                        style={{ marginLeft: 8 }}
                      />
                    </>
                  )}
                  {!gift && (
                    <DSPrimaryButton
                      onClick={() => {
                        window.open(
                          `https://studio.klydoclock.com/gift?clockId=${data.id}`,
                        );
                      }}
                    >
                      Create Gift
                    </DSPrimaryButton>
                  )}
                </DSCardContent>
              </DSCard>
            </DSBox>
            <DeviceMomentsCard deviceId={data.id} />
            <DSBox>
              <DSCard sx={[sectionCardSx, { mb: 2 }]}>
                <DSSectionHeading>Sound</DSSectionHeading>
                <DSCardContent>
                  <DSBox
                    title="Switch chimes sound on/off"
                    sx={{ display: "flex", ml: "20px" }}
                  >
                    <SimpleSwitch
                      title="Chimes"
                      on={undefined}
                      default={data.chimes ? data.chimes.value : true}
                      onClick={(on) =>
                        firebaseService
                          .updateConfig(data.id, "chimes", on)
                          .then(
                            () =>
                              (data.chimes = {
                                time: new Date(),
                                value: on,
                              }),
                          )
                      }
                    />
                  </DSBox>
                  {data.version && data.version >= 592.21 ? (
                    <DSBox title="Chime mode: only chimes / only strikes / both">
                      <SimpleDropdown
                        title="Chime Mode: "
                        list={ChimeMode}
                        default={
                          data.chimeMode ? data.chimeMode.value : ChimeMode[0]
                        }
                        onSelect={(s: string) => {
                          firebaseService
                            .updateConfig(data.id, "chimeMode", s)
                            .then(
                              () =>
                                (data.chimeMode = {
                                  time: new Date(),
                                  value: s,
                                }),
                            );
                        }}
                      />
                    </DSBox>
                  ) : (
                    <DSBox
                      title="Switch Hourly Strikes on/off"
                      sx={{ display: "flex", ml: "20px" }}
                    >
                      <SimpleSwitch
                        title="Hourly Strikes"
                        on={undefined}
                        default={!!data?.hourlyStrikes?.value}
                        disabled={!data.version || data.version < 480.4}
                        onClick={() =>
                          firebaseService.updateConfig(
                            data.id,
                            "hourlyStrikes",
                            !data?.hourlyStrikes?.value,
                          )
                        }
                      />
                    </DSBox>
                  )}
                  <DSBox title="Chimes volume">
                    <SimpleBar
                      title="Chimes Volume:"
                      max={100}
                      default={
                        data.chimesVol
                          ? isNewVolumeRange
                            ? data.chimesVol.value
                            : data.chimesVol.value * 100
                          : 100
                      }
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        const saveValue = isNewVolumeRange
                          ? value
                          : value / 100;
                        firebaseService
                          .updateConfig(data.id, "chimesVol", saveValue)
                          .then(
                            () =>
                              (data.chimesVol = {
                                time: new Date(),
                                value: saveValue,
                              }),
                          );
                      }}
                    />
                  </DSBox>
                  <DSBox
                    title="Switch ticks sound on/off"
                    sx={{ display: "flex", ml: "20px" }}
                  >
                    <SimpleSwitch
                      title="Ticks"
                      default={data.ticks ? data.ticks.value : true}
                      onClick={(on: boolean) => {
                        firebaseService
                          .updateConfig(data.id, "ticks", on)
                          .then(
                            () =>
                              (data.ticks = { time: new Date(), value: on }),
                          );
                      }}
                      on={undefined}
                    />
                  </DSBox>
                  <DSBox title="Ticks volume">
                    <SimpleBar
                      title="Ticks Volume"
                      max={100}
                      default={
                        data.ticksVol
                          ? isNewVolumeRange
                            ? data.ticksVol.value
                            : data.ticksVol.value * 100
                          : 100
                      }
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        const saveValue = isNewVolumeRange
                          ? value
                          : value / 100;
                        firebaseService
                          .updateConfig(data.id, "ticksVol", saveValue)
                          .then(
                            () =>
                              (data.ticksVol = {
                                time: new Date(),
                                value: saveValue,
                              }),
                          );
                      }}
                    />
                  </DSBox>
                  {data.version && data.version >= 592.21 && (
                    <>
                      <DSBox
                        title="Switch klydos sound on/off"
                        sx={{ display: "flex", ml: "20px" }}
                      >
                        <SimpleSwitch
                          title="Klydos Sound"
                          on={undefined}
                          default={data.klydosSound?.value ?? false}
                          onClick={(on) =>
                            firebaseService
                              .updateConfig(data.id, "klydosSound", on)
                              .then(
                                () =>
                                  (data.klydosSound = {
                                    time: new Date(),
                                    value: on,
                                  }),
                              )
                          }
                        />
                      </DSBox>
                      <DSBox title="Player volume">
                        <SimpleBar
                          title="Player Volume"
                          max={100}
                          default={
                            data.playerVolume ? data.playerVolume.value : 100
                          }
                          onChange={(e) => {
                            const value = parseInt(e.target.value);
                            firebaseService
                              .updateConfig(data.id, "playerVolume", value)
                              .then(
                                () =>
                                  (data.playerVolume = {
                                    time: new Date(),
                                    value: value,
                                  }),
                              );
                          }}
                        />
                      </DSBox>
                    </>
                  )}
                  <DSBox
                    title="Switch ticks sound on/off"
                    sx={{ display: "flex", ml: "20px" }}
                  >
                    <SimpleSwitch
                      title="Silent times"
                      default={
                        data.quietTimesOn ? data.quietTimesOn.value : true
                      }
                      onClick={(on: boolean) => {
                        firebaseService
                          .updateConfig(data.id, "quietTimesOn", on)
                          .then(
                            () =>
                              (data.quietTimesOn = {
                                time: new Date(),
                                value: on,
                              }),
                          );
                      }}
                      on={undefined}
                    />
                  </DSBox>
                  <DSBox title="Silent Times">
                    <DSBox
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 2,
                        pl: "20px",
                      }}
                    >
                      <DSBox>
                        <SimpleTimePicker
                          title={"From:"}
                          onChange={(e) => {
                            firebaseService
                              .updateConfig(data.id, "quietTimes", {
                                from: e.target.value,
                                until: data.quietTimes
                                  ? data.quietTimes.value.until
                                  : "07:00",
                              })
                              .then(
                                () =>
                                  (data.quietTimes = {
                                    time: new Date(),
                                    value: {
                                      from: e.target.value,
                                      until: data.quietTimes
                                        ? data.quietTimes.value.until
                                        : "07:00",
                                    },
                                  }),
                              );
                          }}
                          default={
                            data.quietTimes
                              ? data.quietTimes.value.from
                              : "22:00"
                          }
                        />
                      </DSBox>
                      <DSBox>
                        <SimpleTimePicker
                          title={"Until:"}
                          onChange={(e) => {
                            // Unset, the clock runs 22:00-07:00 (firmware
                            // SyncedSettings.kt), the same default the From
                            // picker shows.
                            firebaseService
                              .updateConfig(data.id, "quietTimes", {
                                from: data.quietTimes
                                  ? data.quietTimes.value.from
                                  : "22:00",
                                until: e.target.value,
                              })
                              .then(
                                () =>
                                  (data.quietTimes = {
                                    time: new Date(),
                                    value: {
                                      from: data.quietTimes
                                        ? data.quietTimes.value.from
                                        : "22:00",
                                      until: e.target.value,
                                    },
                                  }),
                              );
                          }}
                          default={
                            data.quietTimes
                              ? data.quietTimes.value.until
                              : "07:00"
                          }
                        />
                      </DSBox>
                    </DSBox>
                  </DSBox>
                  {data.masterSoundMixer?.value && (
                    <DSBox
                      title="Master Sound Mixer Configuration (Read-Only)"
                      sx={{ ml: "20px", mt: "10px" }}
                    >
                      <DSBox
                        sx={{
                          p: "10px",
                          borderRadius: "4px",
                        }}
                      >
                        <DSTypography
                          variant="subtitle2"
                          sx={{ mb: "10px", fontWeight: "bold" }}
                        >
                          Master Sound Mixe:
                        </DSTypography>
                        {Object.entries(data.masterSoundMixer.value).map(
                          ([key, value]) => (
                            <DSBox
                              key={key}
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                py: "5px",
                              }}
                            >
                              <span>{key}:</span>
                              <DSBox
                                sx={{
                                  fontWeight: "bold",
                                  color: value ? "success.main" : "error.main",
                                }}
                              >
                                {value ? "ON" : "OFF"}
                              </DSBox>
                            </DSBox>
                          ),
                        )}
                      </DSBox>
                    </DSBox>
                  )}
                </DSCardContent>
              </DSCard>
            </DSBox>
            <DSBox>
              <DSCard sx={[sectionCardSx, { mb: 2 }]}>
                <DSSectionHeading>Dev</DSSectionHeading>
                <DSCardContent>
                  <DSBox
                    title="Switch dev mode in device on/off"
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      ml: "20px",
                    }}
                  >
                    <SimpleSwitch
                      title="Dev"
                      on={undefined}
                      default={!hideDev}
                      disabled={
                        !!data.version && data.version >= DEV_MODE_MIN_VERSION
                      }
                      onClick={(on) =>
                        firebaseService
                          .updateConfig(data.id, "hideDev", on ? false : true)
                          .then(
                            () =>
                              (data.hideDev = {
                                time: new Date(),
                                value: !on,
                              }),
                          )
                      }
                    />
                    <DSTypography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      * Dev switch for versions &lt; {DEV_MODE_MIN_VERSION}; Dev
                      Mode from version &ge; {DEV_MODE_MIN_VERSION}
                    </DSTypography>
                  </DSBox>
                  <DSBox title="Dev mode: off / dev (dev menu + ADB) / ADB only">
                    <VersionTooltip
                      active={
                        !data.version || data.version < DEV_MODE_MIN_VERSION
                      }
                      message={`Available in version ≥ ${DEV_MODE_MIN_VERSION}`}
                    >
                      <SimpleDropdown
                        title="Dev Mode: "
                        list={Object.values(DevMode)}
                        default={
                          data.devMode
                            ? data.devMode.value
                            : hideDev
                              ? DevMode.OFF
                              : DevMode.DEV
                        }
                        disabled={
                          !data.version || data.version < DEV_MODE_MIN_VERSION
                        }
                        onSelect={(s: string) => {
                          firebaseService
                            .updateConfig(data.id, "devMode", s)
                            .then(() => {
                              data.devMode = {
                                time: new Date(),
                                value: s as DevMode,
                              };
                            })
                            .catch((e) =>
                              console.error("Failed to update devMode", e),
                            );
                        }}
                      />
                    </VersionTooltip>
                  </DSBox>
                  <DSBox
                    title="Mark device as artist clock"
                    sx={{ display: "flex", ml: "20px" }}
                  >
                    <SimpleSwitch
                      title="Artist"
                      on={undefined}
                      default={data.isArtist?.value ?? false}
                      onClick={(on) =>
                        firebaseService
                          .updateConfig(data.id, "isArtist", on)
                          .then(
                            () =>
                              (data.isArtist = {
                                time: new Date(),
                                value: on,
                              }),
                          )
                      }
                    />
                  </DSBox>
                  <DSBox
                    title="Switch meta window in device on/off"
                    sx={{ display: "flex", ml: "20px" }}
                  >
                    <SimpleSwitch
                      title="Show metadata"
                      on={["", ""]}
                      default={data.meta ? data.meta.value : false}
                      onClick={(on) =>
                        firebaseService
                          .updateConfig(data.id, "meta", on)
                          .then(
                            () => (data.meta = { time: new Date(), value: on }),
                          )
                      }
                    />
                  </DSBox>
                  <DSBox
                    title="join our BETA program"
                    sx={{ display: "flex", ml: "20px" }}
                  >
                    <SimpleSwitch
                      title="Join BETA"
                      on={["", ""]}
                      default={data.beta ?? false}
                      onClick={(on) =>
                        firebaseService
                          .updateConfig(data.id, "beta", on)
                          .then(() => (data.beta = on))
                      }
                    />
                  </DSBox>
                  <DSBox
                    title="Mark device as retail device"
                    sx={{ display: "flex", ml: "20px" }}
                  >
                    <SimpleSwitch
                      title="Retail Mode"
                      on={undefined}
                      default={data.isRetail?.value ?? false}
                      disabled={!data.version}
                      onClick={(on) =>
                        firebaseService
                          .updateConfig(data.id, "isRetail", on)
                          .then(
                            () =>
                              (data.isRetail = {
                                time: new Date(),
                                value: on,
                              }),
                          )
                      }
                    />
                    <span color="red">
                      Retail toggle is working only from version 502.6 and above
                    </span>
                  </DSBox>
                  <DSBox
                    title="Lab-onboarded clock: skips the registration QR and asks the user to connect to Wi-Fi and register"
                    sx={{ display: "flex", ml: "20px" }}
                  >
                    <SimpleSwitch
                      title="Pre-Handled Edition"
                      on={undefined}
                      default={data.isPreHandledEditionClock?.value ?? false}
                      onClick={(on) =>
                        firebaseService
                          .updateConfig(data.id, "isPreHandledEditionClock", on)
                          .then(
                            () =>
                              (data.isPreHandledEditionClock = {
                                time: new Date(),
                                value: on,
                              }),
                          )
                      }
                    />
                  </DSBox>
                  <DSBox
                    title="Set by the clock when a registration QR timed out unregistered. Blocks the Pre-Handled Edition wizard; turn off to bring the wizard back"
                    sx={{ display: "flex", ml: "20px" }}
                  >
                    <SimpleSwitch
                      title="Skipped Registration"
                      on={undefined}
                      default={data.skippedRegistration?.value ?? false}
                      onClick={(on) =>
                        firebaseService
                          .updateConfig(data.id, "skippedRegistration", on)
                          .then(
                            () =>
                              (data.skippedRegistration = {
                                time: new Date(),
                                value: on,
                              }),
                          )
                      }
                    />
                  </DSBox>
                  <DSBox
                    title="Hard version"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      ml: "20px",
                    }}
                  >
                    <SimpleSwitch
                      title="Hard Version"
                      on={undefined}
                      default={!!data.hardVersion}
                      onClick={(on) => {
                        setHardVersionEnabled(on);
                        if (!on) {
                          firebaseService
                            .updateField(data.id, "hardVersion", null)
                            .then(() => refetchLastVersion());
                          setHardVersionValue("");
                          setHardVersionPermanent(false);
                          setHardVersionModified(false);
                        }
                      }}
                    />
                    <DSTextField
                      size="small"
                      sx={{ ml: "10px", width: 100 }}
                      value={hardVersionValue}
                      onChange={(e) => {
                        setHardVersionValue(e.target.value);
                        setHardVersionModified(true);
                      }}
                      disabled={!hardVersionEnabled}
                    />
                    {hardVersionEnabled && (
                      <DSButton
                        sx={{ ml: "10px" }}
                        size="small"
                        variant={
                          hardVersionPermanent ? "contained" : "outlined"
                        }
                        color={hardVersionPermanent ? "primary" : "secondary"}
                        onClick={() => {
                          setHardVersionPermanent(!hardVersionPermanent);
                          setHardVersionModified(true);
                        }}
                      >
                        Permanent{hardVersionPermanent ? ": ON" : ": OFF"}
                      </DSButton>
                    )}
                    {hardVersionModified && (
                      <DSPrimaryButton
                        sx={{ ml: "10px" }}
                        size="small"
                        onClick={() => {
                          firebaseService
                            .updateField(data.id, "hardVersion", {
                              version: Number(hardVersionValue),
                              permanent: hardVersionPermanent,
                            })
                            .then(() => refetchLastVersion());
                          setHardVersionModified(false);
                        }}
                      >
                        ✓
                      </DSPrimaryButton>
                    )}
                    {firebaseService.general && (
                      <DSBox
                        sx={{
                          ml: "15px",
                          fontSize: "12px",
                          color: "text.secondary",
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        <span>Latest: {firebaseService.general.last}</span>
                        <span>Public: {firebaseService.general.public}</span>
                        <span>Beta: {firebaseService.general.beta}</span>
                      </DSBox>
                    )}
                  </DSBox>
                </DSCardContent>
              </DSCard>
            </DSBox>
            <DSBox>
              <DeviceProvider
                deviceId={row.id}
                onClose={() => setShowModal(false)}
              >
                <DeviceFavoriteCard />
                <DeviceDefaultChimeCard />
                <DevicePendingKlydosCard />
              </DeviceProvider>

              <DSCard sx={[sectionCardSx, { pb: "15px", mb: 2 }]}>
                <DSSectionHeading>
                  {`Feeds ${data.feeds?.value ? `(${filteredFeedIds.length}${feedsSearch.trim() ? ` / ${data.feeds.value.length}` : ""})` : ""}`}
                </DSSectionHeading>
                <DSCardContent>
                  {data.feeds?.value && data.feeds.value.length > 0 ? (
                    <>
                      <DSTextField
                        type="text"
                        placeholder="Search feeds..."
                        value={feedsSearch}
                        size="small"
                        fullWidth
                        onChange={(e) => {
                          setFeedsSearch(e.target.value);
                          setCurrentFeedsPage(0);
                        }}
                        sx={{ mb: "10px" }}
                      />
                      {filteredFeedIds
                        .slice(currentFeedsPage * 5, (currentFeedsPage + 1) * 5)
                        .map((klydoId: string) => (
                          <KlydoListItem
                            key={klydoId}
                            klydoId={klydoId}
                            canRemove={false}
                          />
                        ))}
                      {filteredFeedIds.length > 5 && (
                        <DSBox
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            mt: "15px",
                            gap: 1,
                          }}
                        >
                          <DSIconButton
                            onClick={() =>
                              setCurrentFeedsPage((prev: number) =>
                                Math.max(prev - 1, 0),
                              )
                            }
                            disabled={currentFeedsPage === 0}
                          >
                            <NavigateBeforeIcon />
                          </DSIconButton>
                          <DSTypography>{currentFeedsPage}</DSTypography>
                          <DSIconButton
                            onClick={() =>
                              setCurrentFeedsPage((prev: number) =>
                                Math.min(
                                  prev + 1,
                                  Math.ceil(filteredFeedIds.length / 5) - 1,
                                ),
                              )
                            }
                            disabled={
                              currentFeedsPage ===
                              Math.ceil(filteredFeedIds.length / 5) - 1
                            }
                          >
                            <NavigateNextIcon />
                          </DSIconButton>
                        </DSBox>
                      )}
                      {feedsSearch.trim() && filteredFeedIds.length === 0 && (
                        <DSBox
                          sx={{ textAlign: "center", color: "text.secondary" }}
                        >
                          No matching feeds
                        </DSBox>
                      )}
                    </>
                  ) : (
                    <DSBox
                      sx={{ textAlign: "center", color: "text.secondary" }}
                    >
                      No feeds configured
                    </DSBox>
                  )}
                </DSCardContent>
              </DSCard>
            </DSBox>
          </DSBox>

          <DSBox
            sx={{
              gridColumn: "1 / -1",
              display: "grid",
              gridTemplateColumns: { xxs: "1fr", md: "1fr 2fr" },
              gap: 2,
              alignItems: "start",
            }}
          >
            <DSBox>
              <DSCard sx={[sectionCardSx, { mb: 2 }]}>
                <DSSectionHeading>Time Offset</DSSectionHeading>
                <DSCardContent>
                  <DSBox
                    title="Set time offset of clock in minutes"
                    sx={{ mb: "10px", ml: "20px" }}
                  >
                    <DSBox
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <FieldLabel>Time Offset (in minutes):</FieldLabel>
                      <DSTextField
                        size="small"
                        onChange={(e) => setTimeOffsetInput(e.target.value)}
                        defaultValue={timeOffset}
                        type="text"
                        error={timeOffsetInvalid}
                        helperText={
                          timeOffsetInvalid
                            ? "Whole minutes, -1440 to 1440"
                            : undefined
                        }
                      />
                    </DSBox>
                  </DSBox>
                </DSCardContent>
              </DSCard>
            </DSBox>
            <DSBox>
              <DSCard sx={[sectionCardSx, { mb: 2 }]}>
                <DSSectionHeading>Updates</DSSectionHeading>
                <DSCardContent>
                  <DevicesCommands
                    devicesIds={[data.id]}
                    deviceVersion={data.version}
                    deviceLastVersion={lastVersionNumber}
                    hardVersionValue={
                      hardVersionEnabled ? hardVersionValue : ""
                    }
                  />
                </DSCardContent>
              </DSCard>
            </DSBox>
          </DSBox>

          <DSBox
            sx={{
              gridColumn: "1 / -1",
              display: "grid",
              gridTemplateColumns: { xxs: "1fr", md: "1fr 2fr" },
              gap: 2,
              alignItems: "start",
            }}
          >
            <DevicePendingCommandsCard deviceId={data.id} />
            <DeviceCommandsHistoryCard deviceId={data.id} />
          </DSBox>

          <DSBox sx={{ gridColumn: "1 / -1" }}>
            <DeviceErrorsCard deviceId={row.id} />
          </DSBox>
        </DSBox>
      </DSDialogContent>
      <DSDialogActions sx={{ justifyContent: "flex-end" }}>
        <DSButton
          variant="text"
          type="button"
          onClick={() => setShowModal(false)}
        >
          OK
        </DSButton>
      </DSDialogActions>
      <DSDialog
        open={showUpdateModal}
        onClose={() => setShowUpdateModal(false)}
        fullWidth
        maxWidth="sm"
      >
        <DSDialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Update App
          <DSIconButton
            aria-label="close"
            onClick={() => setShowUpdateModal(false)}
          >
            <CloseIcon />
          </DSIconButton>
        </DSDialogTitle>
        <DSDialogContent>
          <DSBox
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
              mt: 1,
            }}
          >
            <b>Update App: </b>
            <DSTextField
              label="Version Number"
              size="small"
              onChange={(e) => {
                setUpdateVersion(parseInt(e.target.value));
              }}
              defaultValue={updateVersion}
              type="text"
            />
          </DSBox>
          <DSBox sx={{ display: "flex", flexDirection: "column", mt: 1 }}>
            <DSFormControlLabel
              control={
                <DSCheckBox
                  checked={updateDevApp}
                  onChange={() => {
                    setUpdateDevApp(!updateDevApp);
                  }}
                />
              }
              label={<b>Dev App</b>}
            />
            <DSFormControlLabel
              control={
                <DSCheckBox
                  checked={!updateDevApp}
                  onChange={() => {
                    setUpdateDevApp(!updateDevApp);
                  }}
                />
              }
              label={<b>Public App</b>}
            />
          </DSBox>
        </DSDialogContent>
        <DSDialogActions sx={{ justifyContent: "flex-end" }}>
          <DSPrimaryButton
            onClick={async () => {
              setUpdateAppLoader(true);
              await firebaseService.sendComandToClock(
                data.id,
                "updateApp",
                updateVersion === firebaseService.general?.last
                  ? ""
                  : updateVersion + "",
              );
              setUpdateAppLoader(false);
              setShowUpdateModal(false);
            }}
          >
            {updateAppLoader ? (
              <>
                <DSCircularProgress size={20} sx={{ mr: 1 }} /> Update
              </>
            ) : (
              "Update"
            )}
          </DSPrimaryButton>
        </DSDialogActions>
      </DSDialog>
      {showKlydoViewingModal && (
        <DSDialog
          open={showKlydoViewingModal}
          onClose={() => setShowKlydoViewingModal(false)}
          fullWidth
          maxWidth="lg"
        >
          <DSDialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            Klydo Viewing History
            <DSIconButton
              aria-label="close"
              onClick={() => setShowKlydoViewingModal(false)}
            >
              <CloseIcon />
            </DSIconButton>
          </DSDialogTitle>

          {klydoAnalyticData && (
            <MsTable
              id={tableId}
              rowBuild={tableRows}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              search={(tableRow: any, val: string) => {
                return (
                  tableRow.klydo?.tags?.find(
                    (t: string) => t.toLowerCase() === val,
                  ) | tableRow.klydo?.idf.includes(val) ||
                  tableRow.klydo?.id.includes(val) ||
                  tableRow.klydo?.author?.toLowerCase().includes(val) ||
                  tableRow.klydo?.name?.toLowerCase().includes(val)
                );
              }}
              initSort={"clockTime"}
              data={klydoAnalyticData}
              cols={tableColumn}
              title=""
            />
          )}
          {!klydoAnalyticData && (
            <DSBox
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                p: 6,
              }}
            >
              <DSCircularProgress />
            </DSBox>
          )}
        </DSDialog>
      )}
    </>
  );
}

export default DeviceModal;
